/**
 * App background context — React-обёртка над модулем `appBackground`.
 *
 * Источник правды — localStorage (см. `appBackground.ts`). Провайдер при монтировании
 * подстраховывает применение сохранённого цвета (на случай, если anti-FOUC скрипт
 * в `index.html` не отработал), и предоставляет `setColor` / `reset` для UI.
 *
 * Каждая из трёх точек применения зовёт `refreshThemeColor()`: системная полоса над вебвью
 * (статус-бар установленного кабинета, адресная строка браузера телефона) красится из тех же
 * переменных, что и экран, но CSS до неё не достаёт — только `<meta name="theme-color">`.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  DEFAULT_BG,
  applyBackground,
  normalizeHex,
  readStored,
  resetBackground,
  writeStored,
} from './appBackground';
import { refreshThemeColor } from './themeColor';

interface AppBackgroundValue {
  /** Текущий применённый цвет канвы (`#rrggbb`). */
  color: string;
  /** true, если выбран не дефолтный фон. */
  isCustom: boolean;
  /** Применить и сохранить цвет. */
  setColor: (hex: string) => void;
  /** Вернуть стандартный фон. */
  reset: () => void;
}

const AppBackgroundContext = createContext<AppBackgroundValue>({
  color: DEFAULT_BG,
  isCustom: false,
  setColor: () => {},
  reset: () => {},
});

export function AppBackgroundProvider({ children }: { children: ReactNode }): JSX.Element {
  const [color, setColorState] = useState<string>(() => readStored() ?? DEFAULT_BG);

  // Подстраховка: синхронизируем DOM с сохранённым значением на старте.
  // (Основное применение делает anti-FOUC скрипт в index.html ещё до React.)
  useEffect(() => {
    applyBackground(color);
    refreshThemeColor();
    // Только при монтировании.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setColor = useCallback((hex: string) => {
    const applied = applyBackground(hex);
    // Полоса статус-бара берёт цвет из тех же переменных, что и экран, — значит пересчитывается
    // вместе с ними. Без этой строки выбранный «Тёплый» доходил бы до карточек, но не до верхней
    // кромки установленного приложения.
    refreshThemeColor();
    setColorState(applied);
    writeStored(applied);
  }, []);

  const reset = useCallback(() => {
    resetBackground();
    refreshThemeColor();
    setColorState(DEFAULT_BG);
    writeStored(null);
  }, []);

  const isCustom = normalizeHex(color) !== normalizeHex(DEFAULT_BG);

  return (
    <AppBackgroundContext.Provider value={{ color, isCustom, setColor, reset }}>
      {children}
    </AppBackgroundContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAppBackground(): AppBackgroundValue {
  return useContext(AppBackgroundContext);
}
